import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Star, Loader2, CheckCircle } from 'lucide-react'
import api from '../lib/api'
import { toast } from '../lib/toast'
import Turnstile, { turnstileEnabled } from './Turnstile'

export interface RatingSummary {
  tool: string
  count: number
  average: number
  distribution?: Record<string, number>
  comments?: { rating: number; comment: string; created_at: string }[]
  my_rating?: number | null
  my_comment_pending?: boolean
}

/** Read-only zobrazení průměru hvězdičkami (podporuje desetinné přes překryv). */
export function RatingStars({ value, size = 16, className = '' }: { value: number; size?: number; className?: string }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100))
  return (
    <span className={`relative inline-flex ${className}`} style={{ width: size * 5, height: size }} aria-hidden="true">
      <span className="flex text-gray-300 dark:text-white/20">
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} style={{ width: size, height: size }} fill="currentColor" strokeWidth={0} />)}
      </span>
      <span className="absolute inset-0 flex overflow-hidden text-amber-400" style={{ width: `${pct}%` }}>
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} style={{ width: size, height: size, minWidth: size }} fill="currentColor" strokeWidth={0} />)}
      </span>
    </span>
  )
}

/** Kompaktní hodnocení pro kartu nástroje: „★ 4,8 (213)". Nic nezobrazí, když 0 hlasů. */
export function RatingBadge({ summary, className = '' }: { summary?: { average: number; count: number }; className?: string }) {
  const { i18n } = useTranslation()
  if (!summary || summary.count === 0) return null
  return (
    <span className={`inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 ${className}`}>
      <Star className="w-3.5 h-3.5 text-amber-400" fill="currentColor" strokeWidth={0} />
      <span className="font-semibold text-gray-700 dark:text-gray-200">{summary.average.toLocaleString(i18n.language)}</span>
      <span>({summary.count})</span>
    </span>
  )
}

/** Interaktivní widget: zobrazí průměr + umožní ohodnotit (hvězdy + volitelný komentář). */
export default function ToolRating({ tool, className = '' }: { tool: string; className?: string }) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const [hover, setHover] = useState(0)
  const [picked, setPicked] = useState(0)
  const [comment, setComment] = useState('')
  const [captcha, setCaptcha] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [pending, setPending] = useState(false)

  const { data } = useQuery<RatingSummary>({
    queryKey: ['tool-rating', tool],
    queryFn: () => api.get(`/tools/${tool}/rating`).then((r) => r.data),
    staleTime: 60_000,
  })

  const alreadyRated = !!data?.my_rating
  const active = hover || picked

  const submit = async () => {
    if (!picked) return
    setSubmitting(true)
    try {
      const { data } = await api.post(`/tools/${tool}/rating`, { rating: picked, comment: comment.trim() || undefined, turnstile_token: captcha })
      setPending(!!data?.comment_pending)
      setDone(true)
      toast.success(t('rating.thanks', { defaultValue: 'Díky za hodnocení!' }))
      qc.invalidateQueries({ queryKey: ['tool-rating', tool] })
      qc.invalidateQueries({ queryKey: ['tool-ratings-all'] })
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={`rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-white/5 p-5 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{t('rating.title', { defaultValue: 'Ohodnoťte tento nástroj' })}</h3>
        {data && data.count > 0 && (
          <div className="flex items-center gap-2">
            <RatingStars value={data.average} />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              <strong>{data.average.toLocaleString(i18n.language)}</strong>{' '}
              <span className="text-gray-400">({t('rating.count', { count: data.count, defaultValue: '{{count}} hodnocení' })})</span>
            </span>
          </div>
        )}
      </div>

      {done || alreadyRated ? (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <CheckCircle className="w-4 h-4" />
          {(pending || data?.my_comment_pending)
            ? t('rating.pending', { defaultValue: 'Děkujeme! Hvězdy jsou započítané, komentář se zobrazí po schválení.' })
            : t('rating.already', { defaultValue: 'Děkujeme, vaše hodnocení je uložené.' })}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Výběr hvězd */}
          <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n} type="button"
                onMouseEnter={() => setHover(n)}
                onClick={() => setPicked(n)}
                aria-label={t('rating.stars_n', { count: n, defaultValue: '{{count}} hvězd' })}
                className="p-0.5 transition-transform hover:scale-110"
              >
                <Star className={`w-7 h-7 ${n <= active ? 'text-amber-400' : 'text-gray-300 dark:text-white/20'}`} fill="currentColor" strokeWidth={0} />
              </button>
            ))}
          </div>

          {picked > 0 && (
            <>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder={t('rating.comment_ph', { defaultValue: 'Volitelný komentář, co se vám líbí nebo chybí?' })}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <Turnstile onVerify={setCaptcha} className="flex" />
              <button
                onClick={submit}
                disabled={submitting || (turnstileEnabled() && !captcha)}
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary-700 transition disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('rating.submit', { defaultValue: 'Odeslat hodnocení' })}
              </button>
            </>
          )}
        </div>
      )}

      {/* Pár posledních komentářů */}
      {data?.comments && data.comments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/10 space-y-3">
          {data.comments.slice(0, 3).map((c, i) => (
            <div key={i} className="text-sm">
              <RatingStars value={c.rating} size={13} className="mb-0.5" />
              <p className="text-gray-600 dark:text-gray-300">{c.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
